import { fx } from "../inspector/bindings";
import type { SectionDef } from "../inspector/schema";

/**
 * Anti-Aliasing tab — Progressive refinement + Temporal AA + the Velocity
 * buffer, mirroring iJewel's three Anti-aliasing sections. Progressive and TAA
 * ride PostFXSettings.progressive/taa; the velocity buffer has its own toggle
 * (renders independently of TAA, e.g. for the velocity debug view).
 */

export const antialiasingSections: SectionDef[] = [
  {
    id: "progressive",
    title: "Progressive",
    enabledBadge: fx((s) => s.progressive.enabled, (v) => ({ progressive: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Refine When Idle", binding: fx((s) => s.progressive.enabled, (v) => ({ progressive: { enabled: v } })) },
      { id: "maxFrames", kind: "slider", label: "Frame count", min: 8, max: 64, step: 1, binding: fx((s) => s.progressive.maxFrames, (v) => ({ progressive: { maxFrames: v } })) },
      { id: "jitter", kind: "checkbox", label: "Jitter", binding: fx((s) => s.progressive.jitter, (v) => ({ progressive: { jitter: v } })) },
    ],
  },
  {
    id: "taa",
    title: "Temporal AA",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.taa.enabled, (v) => ({ taa: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.taa.enabled, (v) => ({ taa: { enabled: v } })) },
      { id: "feedbackMin", kind: "slider", label: "Feedback (motion)", min: 0, max: 0.98, step: 0.0001, binding: fx((s) => s.taa.feedbackMin, (v) => ({ taa: { feedbackMin: v } })) },
      { id: "feedbackMax", kind: "slider", label: "Feedback (rest)", min: 0, max: 0.99, step: 0.0001, binding: fx((s) => s.taa.feedbackMax, (v) => ({ taa: { feedbackMax: v } })) },
      { id: "stableNoise", kind: "checkbox", label: "Stable Noise (Use Total Frame Count)", binding: fx((s) => s.taa.stableNoise, (v) => ({ taa: { stableNoise: v } })) },
      { id: "taa-note", kind: "note", label: "", text: "Active during motion; idle frames use progressive refinement. The blend runs from Feedback (rest) toward Feedback (motion) as a pixel's screen-space speed rises — lower Feedback (motion) if orbiting looks smeared." },
    ],
  },
  {
    id: "velocity",
    title: "Velocity Buffer (TAA)",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.velocityBuffer.enabled, (v) => ({ velocityBuffer: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.velocityBuffer.enabled, (v) => ({ velocityBuffer: { enabled: v } })) },
      { id: "vel-note", kind: "note", label: "", text: "Per-pixel motion vectors for TAA / the velocity debug view. Renders automatically while TAA is on." },
    ],
  },
  {
    // Adaptive dynamic-resolution notch floor during sustained camera motion
    // (v32). Rides the existing setPostFX (no new method name).
    id: "motion",
    title: "Motion Quality",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.motion.enabled, (v) => ({ motion: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.motion.enabled, (v) => ({ motion: { enabled: v } })) },
      { id: "minScale", kind: "slider", label: "Min Scale (floor)", min: 0.3, max: 1, step: 0.01, binding: fx((s) => s.motion.minScale, (v) => ({ motion: { minScale: v } })) },
      { id: "motion-note", kind: "note", label: "", text: "Drops render scale one notch at a time while orbiting a heavy scene, then restores full resolution on idle." },
    ],
  },
];
